import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { getPictureAIStatus, getPictureConversionStatus } from "services/pictureServices";
import { RootState } from "store";

const POLLING_INTERVAL = 2000; // 2 seconds
const MAX_POLLING_DURATION = 30000; // 30 seconds

interface PollingState {
  [pictureId: number]: {
    isPolling: boolean;
    error: string | null;
  };
}

const initialState: PollingState = {};

// Async thunk for polling
export const pollPictureConversion = createAsyncThunk(
  "picturePolling/pollConversionStatus",
  async (pictureId: number) => {
    const pollWithTimeout = async (
      statusFetcher: () => Promise<boolean>,
      timeoutMessage: string
    ): Promise<void> => {
      const pollingStartTime = Date.now();

      const poll = async (): Promise<void> => {
        if (Date.now() - pollingStartTime > MAX_POLLING_DURATION) {
          throw new Error(timeoutMessage);
        }

        const done = await statusFetcher();
        if (done) {
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, POLLING_INTERVAL));
        return poll();
      };

      await poll();
    };

    try {
      await pollWithTimeout(
        async () => {
          const { done } = await getPictureConversionStatus(pictureId);
          return done;
        },
        "Conversion timeout exceeded"
      );

      await pollWithTimeout(
        async () => {
          const { done } = await getPictureAIStatus(pictureId);
          return done;
        },
        "AI processing timeout exceeded"
      );

      return { pictureId };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      throw { pictureId, message: errorMessage };
    }
  }
);

const picturePollingSlice = createSlice({
  name: "picturePolling",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(pollPictureConversion.pending, (state, action) => {
        const pictureId = action.meta.arg;
        state[pictureId] = {
          isPolling: true,
          error: null,
        };
      })
      .addCase(pollPictureConversion.fulfilled, (state, action) => {
        const { pictureId } = action.payload;
        if (state[pictureId]) {
          state[pictureId].isPolling = false;
        }
      })
      .addCase(pollPictureConversion.rejected, (state, action) => {
        const { pictureId, message } = action.payload as { pictureId: number; message: string };
        if (state[pictureId]) {
          state[pictureId].isPolling = false;
          state[pictureId].error = message;
        }
      });
  },
});

export const selectPollingState = (state: RootState, pictureId: number) =>
  state.picturePolling[pictureId];

export default picturePollingSlice.reducer;
